import { I18nProvider } from '@contexts/I18nContext'
import { NotificationProvider, useNotifications } from '@contexts/NotificationContext'
import { Global } from '@emotion/react'
import { KeycloakProvider, useKeycloak } from '@lib/keycloak'
import { QueryProvider } from '@lib/react-query'
import { initSentry } from '@lib/sentry'
import * as Sentry from '@sentry/react'
import { RootErrorFallback } from '@shared/components/ErrorFallback'
import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'

import { SuiProvider } from '@hrworks/sui-core'
import { Page } from '@hrworks/sui-core/Page'

import { setKeycloakInstance } from './api/client'
import { router } from './routes/AppRoutes'
import { globalStyles, theme } from './styles'

// Import SmartFace critical styles (CSS variables, fonts, base styles)
import '@hrworks/design-system/styles/critical.css'

// Initialize Sentry before rendering the app
initSentry()

const KeycloakConnector = ({ children }: { children: React.ReactNode }) => {
  const { keycloak } = useKeycloak()

  useEffect(() => {
    if (keycloak) {
      setKeycloakInstance(keycloak)
    }
  }, [keycloak])

  return <>{children}</>
}

// Reads the live notification array from context and passes it to Page so the
// SmartFace layout renders toast notifications.
const NotificationBridge = ({ children }: { children: React.ReactNode }) => {
  const { notifications } = useNotifications()
  return (
    <Page modals={[]} notifications={notifications}>
      {children}
    </Page>
  )
}

const root = createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={RootErrorFallback} showDialog={false}>
      <KeycloakProvider>
        <SuiProvider customTheme={theme} useCustomScrollbars={true} iconSet="streamline">
          <Global styles={globalStyles(theme)} />
          <I18nProvider>
            <NotificationProvider>
              <QueryProvider>
                <KeycloakConnector>
                  <NotificationBridge>
                    <RouterProvider router={router} />
                  </NotificationBridge>
                </KeycloakConnector>
              </QueryProvider>
            </NotificationProvider>
          </I18nProvider>
        </SuiProvider>
      </KeycloakProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
)
